import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Box, BoxProps } from '@apia/theme';
import ReactFocusLock, { FreeFocusInside } from 'react-focus-lock';
import { getVariant } from '@apia/theme';
import { useCombinedRefs } from '@apia/util';
import { useBodyScrollLock } from '../../hooks';

export interface IOverlay extends BoxProps {
  children: React.ReactNode;
  className?: string;
  onOverlayClicked: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Permite pasar un callback que será llamado una vez que el overlay termine
   * de cerrarse.
   */
  onExited: () => void;
  isOpen?: boolean;
  shouldAvoidInitialFocusTrap?: boolean;
  stretch?: boolean;
  transitionDuration?: number;
}

export const Overlay = React.forwardRef<unknown, IOverlay>(
  (
    {
      children,
      className = '',
      onExited,
      onOverlayClicked,
      isOpen: open = false,
      shouldAvoidInitialFocusTrap,
      stretch,
      transitionDuration = 150,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<HTMLDivElement>(null);
    const assignRefs = useCombinedRefs(innerRef, ref);
    useBodyScrollLock(open);
    return (
      <CSSTransition
        in={open}
        timeout={transitionDuration}
        onExited={onExited}
        classNames="overlayAnimation"
        appear
        nodeRef={innerRef}
        unmountOnExit
      >
        <Box
          className={`${className} overlay`}
          ref={assignRefs}
          {...props}
          data-type="overlay"
          role="presentation"
          onClick={React.useCallback(
            (ev: React.MouseEvent<HTMLDivElement>) => {
              if (ev.target === innerRef.current) onOverlayClicked(ev);
            },
            [onOverlayClicked],
          )}
          {...getVariant(
            props.variant ??
              `layout.common.modals.overlay${stretch ? '--stretch' : ''}`,
          )}
        >
          <ReactFocusLock
            autoFocus={shouldAvoidInitialFocusTrap ? false : undefined}
            returnFocus
            crossFrame={false}
          >
            <FreeFocusInside>{children}</FreeFocusInside>
          </ReactFocusLock>
        </Box>
      </CSSTransition>
    );
  },
);

Overlay.displayName = 'Overlay';
